import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  BookOpen, 
  Users, 
  FileText, 
  ClipboardCheck,
  ChevronRight,
  Loader2,
  AlertCircle,
  TrendingUp,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InstructorDashboard() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch instructor's courses
        const coursesRes = await fetch(`${API_URL}/api/portal/courses/instructor/my`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        
        if (coursesRes.ok) {
          const coursesData = await coursesRes.json();
          setCourses(coursesData.courses || []);
          
          // Calculate stats based on courses
          let totalStudents = 0;
          let totalAssignments = 0;
          let pendingGrading = 0;
          
          for (const course of (coursesData.courses || [])) {
            // Get assignments for each course
            const assignRes = await fetch(`${API_URL}/api/portal/assignments/instructor/course/${course.course_id}`, {
              headers: { 'Authorization': `Bearer ${token}` },
              credentials: 'include'
            });
            
            if (assignRes.ok) {
              const assignData = await assignRes.json();
              totalAssignments += assignData.total || 0;
              
              for (const assign of (assignData.assignments || [])) {
                pendingGrading += (assign.total_submissions || 0) - (assign.graded_count || 0);
              }
            }
            
            totalStudents += course.enrolled_count || 0;
          }
          
          setStats({
            totalCourses: coursesData.courses?.length || 0,
            totalStudents,
            totalAssignments,
            pendingGrading
          });
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching instructor data:', err);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    }
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const statCards = [
    { 
      label: 'My Courses', 
      value: stats?.totalCourses || 0, 
      icon: BookOpen, 
      color: 'bg-emerald-500',
      link: '/instructor/courses'
    },
    { 
      label: 'Total Students', 
      value: stats?.totalStudents || 0, 
      icon: Users, 
      color: 'bg-blue-500',
      link: '/instructor/students'
    },
    { 
      label: 'Assignments', 
      value: stats?.totalAssignments || 0, 
      icon: FileText, 
      color: 'bg-purple-500',
      link: '/instructor/assignments'
    },
    { 
      label: 'Pending Grading', 
      value: stats?.pendingGrading || 0, 
      icon: ClipboardCheck, 
      color: 'bg-orange-500',
      link: '/instructor/submissions',
      highlight: stats?.pendingGrading > 0
    },
  ];

  return (
    <div className="space-y-8" data-testid="instructor-dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Welcome back, {user?.name?.split(' ')[0] || 'Instructor'}!
        </h1>
        <p className="text-emerald-100">
          Manage your courses, review submissions, and track student progress.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Link key={stat.label} to={stat.link}>
            <Card className={`hover:shadow-lg transition-shadow cursor-pointer ${stat.highlight ? 'ring-2 ring-orange-400' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    <p className="text-sm text-gray-500">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* My Courses */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold">My Courses</CardTitle>
            <Link to="/instructor/courses">
              <Button variant="ghost" size="sm">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {courses.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No courses assigned yet</p>
            ) : (
              <div className="space-y-3">
                {courses.slice(0, 4).map((course) => (
                  <Link 
                    key={course.course_id} 
                    to={`/instructor/courses/${course.course_id}`}
                    className="block p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{course.title}</p>
                        <p className="text-sm text-gray-500">{course.code}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-emerald-600">{course.enrolled_count || 0} students</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/instructor/assignments" className="block">
              <Button variant="outline" className="w-full justify-start gap-3">
                <FileText className="w-5 h-5 text-emerald-600" />
                <span>Create New Assignment</span>
              </Button>
            </Link>
            <Link to="/instructor/submissions" className="block">
              <Button variant="outline" className="w-full justify-start gap-3">
                <ClipboardCheck className="w-5 h-5 text-orange-600" />
                <span>Review Submissions</span>
                {stats?.pendingGrading > 0 && (
                  <span className="ml-auto bg-orange-100 text-orange-700 text-xs px-2 py-1 rounded-full">
                    {stats.pendingGrading} pending
                  </span>
                )}
              </Button>
            </Link>
            <Link to="/instructor/gradebook" className="block">
              <Button variant="outline" className="w-full justify-start gap-3">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <span>View Gradebook</span>
              </Button>
            </Link>
            <Link to="/instructor/announcements" className="block">
              <Button variant="outline" className="w-full justify-start gap-3">
                <Clock className="w-5 h-5 text-purple-600" />
                <span>Post Announcement</span>
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
